import { useMutation } from "@apollo/client";
import Logo from "../../components/common/Logo";
import { useEffect, useState } from "react";
import "react-phone-input-2/lib/style.css";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ACTIVATE_USER_ACCOUNT } from "src/constants/mutations";

const SuccessIcon = () => (
  <svg
    className="h-8 w-8 text-[#FF6A3D]"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const ErrorIcon = () => (
  <svg
    className="h-8 w-8 text-red-500"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
    />
  </svg>
);

const ArrowLeftIcon = () => (
  <svg
    className="h-4 w-4"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18"
    />
  </svg>
);

const AuthStatusShell = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const navigate = useNavigate();

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-4 py-12"
      style={{ background: "linear-gradient(180deg, #F7F8FA 0%, #EEF1F4 100%)" }}
    >
      <div
        onClick={() => navigate("/")}
        className="flex items-center justify-center gap-x-2 cursor-pointer mb-8"
      >
        <Logo />
        <h1 className="text-2xl font-semibold text-[#111827]">EatsBits</h1>
      </div>

      {children}
    </div>
  );
};

export const ActivateAccount = () => {
  const routeParams = useParams();
  const [message, setMessage] = useState("");

  const [activateUserAccount, { data, loading, error }] = useMutation(
    ACTIVATE_USER_ACCOUNT,
    {
      variables: {
        activateUserAccountInput: {
          activationCode: routeParams.verificationCode,
        },
      },
    }
  );

  useEffect(() => {
    activateUserAccount();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (data) {
      setMessage(data.activateUserAccount.message);
    }
  }, [data]);

  if (loading) {
    return (
      <AuthStatusShell>
        <div className="w-full max-w-[460px] rounded-[14px] bg-white p-8 text-center shadow-[0_12px_30px_rgba(0,0,0,0.08)]">
          <div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-[#FFE0D6] border-t-[#FF6A3D]" />
          <h2
            className="mt-6 text-[28px] font-bold leading-tight text-[#111827]"
            style={{ letterSpacing: "-0.01em" }}
          >
            Activating account
          </h2>
          <p className="mt-3 text-[15px] leading-6 text-[#6B7280]">
            This should only take a moment.
          </p>
        </div>
      </AuthStatusShell>
    );
  }

  if (error) {
    return (
      <AuthStatusShell>
        <div className="w-full max-w-[460px] rounded-[14px] bg-white p-8 text-center shadow-[0_12px_30px_rgba(0,0,0,0.08)]">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-50">
            <ErrorIcon />
          </div>
          <h2
            className="mt-6 text-[28px] font-bold leading-tight text-[#111827]"
            style={{ letterSpacing: "-0.01em" }}
          >
            Activation failed
          </h2>
          <p className="mt-3 text-[15px] leading-6 text-[#6B7280]">
            {error.message}
          </p>
          <Link
            to="/login"
            className="mt-8 inline-flex h-12 w-full items-center justify-center gap-2 rounded-[10px] bg-[#FF6A3D] text-[15px] font-semibold text-white transition-all duration-150 hover:-translate-y-px hover:bg-[#E85C32] active:translate-y-0"
          >
            <ArrowLeftIcon />
            Back to login
          </Link>
        </div>
      </AuthStatusShell>
    );
  }

  return (
    <AuthStatusShell>
      <div className="w-full max-w-[460px] rounded-[14px] bg-white p-8 text-center shadow-[0_12px_30px_rgba(0,0,0,0.08)]">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#FFF0EB]">
          <SuccessIcon />
        </div>

        <h2
          className="mt-6 text-[28px] font-bold leading-tight text-[#111827]"
          style={{ letterSpacing: "-0.01em" }}
        >
          Account activated
        </h2>
        <p className="mt-3 text-[15px] leading-6 text-[#6B7280]">
          {message || "Your EatsBits account is ready. Sign in to continue."}
        </p>

        <Link
          to="/login"
          className="mt-8 inline-flex h-12 w-full items-center justify-center gap-2 rounded-[10px] bg-[#FF6A3D] text-[15px] font-semibold text-white transition-all duration-150 hover:-translate-y-px hover:bg-[#E85C32] active:translate-y-0"
        >
          <ArrowLeftIcon />
          Back to login
        </Link>
      </div>
    </AuthStatusShell>
  );
};
